/**
 * Corte del edificio: cielo, calle, fachada, pisos, elevador, avatares y luz.
 *
 * Lo fijo de cada piso (tapiz, suelo, muebles quietos) se pinta una vez y se
 * guarda; en cada cuadro solo se dibuja lo que se mueve, los avatares y la capa
 * de luz. Los pisos fuera de la vista se saltan.
 */
import { INK, PX, hashId, mix, painter, rgba, shade } from './pixelKit';
import {
    BRICK,
    ELEV_W,
    FEET,
    FLOOR_H,
    ROOF,
    feetY,
    floorTop,
} from './officeMap';
import { CRITTERS, OUTDOOR, PROPS } from './officeProps';
import { blitAvatar } from './avatarSprites';
import { createLightLayer, drawSky, lightFloor, skyAt } from './officeLight';

const BAND = 36;
const DOOR_H = 156;
const CAR_H = 190;
const CONFETTI = ['#ff5ea8', '#5ee0ff', '#ffe14a', '#3dff8a', '#ff8a3c'];
let lightLayer = null;

function wallpaper(ctx, x, y, w, h, kind, color) {
    ctx.fillStyle = color;
    ctx.fillRect(x, y, w, h);
    ctx.save();
    ctx.beginPath();
    ctx.rect(x, y, w, h);
    ctx.clip();
    if (kind === 'stripes') {
        ctx.fillStyle = 'rgba(255,255,255,0.26)';
        for (let col = 0; col < w; col += 18) ctx.fillRect(x + col, y, 9, h);
    } else if (kind === 'tile') {
        ctx.fillStyle = 'rgba(255,255,255,0.34)';
        for (let row = 0; row < h; row += 18) ctx.fillRect(x, y + row, w, 2);
        for (let col = 0; col < w; col += 18) ctx.fillRect(x + col, y, 2, h);
    } else if (kind === 'herring') {
        ctx.fillStyle = 'rgba(255,255,255,0.22)';
        for (let row = 0; row < h; row += 12) {
            for (let col = 0; col < w; col += 12) ctx.fillRect(x + col + (row % 24 ? 6 : 0), y + row, 9, 3);
        }
    } else if (kind === 'dots') {
        ctx.fillStyle = 'rgba(255,255,255,0.4)';
        for (let row = 12; row < h; row += 18) {
            for (let col = 9; col < w; col += 18) ctx.fillRect(x + col + (row % 36 ? 9 : 0), y + row, 3, 3);
        }
    } else if (kind === 'brick') {
        ctx.fillStyle = 'rgba(120,70,40,0.2)';
        for (let row = 0; row < h; row += 12) {
            const shift = (row / 12) % 2 ? 12 : 0;
            ctx.fillRect(x, y + row, w, 2);
            for (let col = -12; col < w; col += 24) ctx.fillRect(x + col + shift, y + row, 2, 12);
        }
    } else if (kind === 'plank') {
        ctx.fillStyle = 'rgba(90,60,30,0.16)';
        for (let col = 0; col < w; col += 21) ctx.fillRect(x + col, y, 2, h);
    } else if (kind === 'bath') {
        ctx.fillStyle = 'rgba(255,255,255,0.5)';
        for (let row = 0; row < h; row += 15) ctx.fillRect(x, y + row, w, 2);
        for (let col = 0; col < w; col += 15) ctx.fillRect(x + col, y, 2, h);
        ctx.fillStyle = 'rgba(60,140,130,0.5)';
        ctx.fillRect(x, y + 60, w, 6);
    }
    if (kind === 'wainscot' || kind === 'stripes' || kind === 'dots') {
        ctx.fillStyle = kind === 'wainscot' ? '#b89a6a' : 'rgba(60,40,20,0.16)';
        ctx.fillRect(x, y + h - 66, w, 66);
        ctx.fillStyle = kind === 'wainscot' ? '#d8be8e' : 'rgba(255,255,255,0.3)';
        ctx.fillRect(x, y + h - 66, w, 3);
        if (kind === 'wainscot') {
            ctx.fillStyle = '#a4875a';
            for (let col = 12; col < w; col += 48) ctx.fillRect(x + col, y + h - 54, 36, 42);
        }
    }
    ctx.restore();
}

function floorBand(ctx, x, y, w, look) {
    const base = look.floor || '#d7b07a';
    ctx.fillStyle = base;
    ctx.fillRect(x, y, w, BAND);
    if (look.floorStyle === 'checker') {
        ctx.fillStyle = shade(base, -26);
        for (let col = 0; col < w; col += 24) {
            ctx.fillRect(x + col, y, 12, 18);
            ctx.fillRect(x + col + 12, y + 18, 12, 18);
        }
    } else if (look.floorStyle === 'carpet') {
        ctx.fillStyle = shade(base, -18);
        for (let col = 0; col < w; col += 9) ctx.fillRect(x + col, y + ((col / 9) % 4) * 9, 3, 3);
        ctx.fillStyle = shade(base, 20);
        for (let col = 5; col < w; col += 15) ctx.fillRect(x + col, y + 6 + ((col / 5) % 3) * 9, 3, 3);
    } else if (look.floorStyle === 'marble') {
        ctx.fillStyle = shade(base, 18);
        for (let col = 0; col < w; col += 60) ctx.fillRect(x + col, y, 57, BAND);
        ctx.fillStyle = shade(base, -24);
        for (let col = 12; col < w; col += 60) {
            ctx.fillRect(x + col, y + 9, 15, 3);
            ctx.fillRect(x + col + 12, y + 12, 12, 3);
            ctx.fillRect(x + col + 27, y + 24, 12, 3);
        }
    } else {
        ctx.fillStyle = shade(base, -30);
        for (let col = 0; col < w; col += 30) ctx.fillRect(x + col + (col % 60 ? 0 : 15), y, 2, BAND);
        ctx.fillRect(x, y + 17, w, 2);
        ctx.fillStyle = shade(base, 20);
        ctx.fillRect(x, y + 4, w, 2);
    }
    ctx.fillStyle = 'rgba(40,24,10,0.35)';
    ctx.fillRect(x, y, w, 4);
}

function brickColumn(ctx, x, y, w, h, tone) {
    ctx.fillStyle = tone;
    ctx.fillRect(x, y, w, h);
    ctx.fillStyle = shade(tone, -34);
    for (let row = 0; row < h; row += 9) {
        const shift = (row / 9) % 2 ? 9 : 0;
        ctx.fillRect(x, y + row, w, 2);
        for (let col = -9; col < w; col += 18) {
            const bx = x + col + shift;
            if (bx >= x && bx < x + w) ctx.fillRect(bx, y + row, 2, 9);
        }
    }
}

function baseY(map, floor) {
    return floorTop(map, floor.index) + FLOOR_H - FEET;
}

/** Fondo fijo de un piso: paredes, suelo, letreros y muebles sin animación. */
function buildFloorCache(map, floor) {
    const canvas = document.createElement('canvas');
    canvas.width = map.innerW;
    canvas.height = FLOOR_H;
    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingEnabled = false;
    floor.rooms.forEach((room) => {
        const x = room.x - map.innerX;
        wallpaper(ctx, x, 0, room.w, FLOOR_H - BAND, room.look.wallpaper, room.look.wall);
        floorBand(ctx, x, FLOOR_H - BAND, room.w, room.look);
        ctx.fillStyle = shade(room.look.trim, -20);
        ctx.fillRect(x, FLOOR_H - BAND - 5, room.w, 5);
        ctx.fillStyle = '#4a3f38';
        ctx.fillRect(x, 0, room.w, 8);
        ctx.fillStyle = '#6a5c52';
        ctx.fillRect(x, 6, room.w, 2);
        room.items.forEach((item) => {
            const prop = PROPS[item.type];
            if (!prop || !prop.s || prop.layer === 'front') return;
            prop.s(painter(ctx, x + item.x, FLOOR_H - FEET - (item.lift || 0) * PX), item);
        });
        const title = String(room.name || floor.name).slice(0, 18);
        ctx.font = 'bold 10px monospace';
        const plate = Math.ceil(ctx.measureText(title).width) + 12;
        ctx.fillStyle = '#1a140e';
        ctx.fillRect(x + 12, 14, plate, 15);
        ctx.fillStyle = room.alert ? '#ff8a7a' : '#f6e27a';
        ctx.fillText(title, x + 18, 25);
    });
    floor.rooms.forEach((room, index) => {
        if (!index) return;
        const x = room.x - map.innerX;
        ctx.fillStyle = shade(room.look.trim, -34);
        ctx.fillRect(x - 5, 8, 10, FLOOR_H - BAND - DOOR_H - 8);
        ctx.fillStyle = shade(room.look.trim, 10);
        ctx.fillRect(x - 5, FLOOR_H - BAND - DOOR_H - 4, 10, 4);
    });
    return canvas;
}

function itemOn(item, room, frame) {
    if (item.use) return frame.used.has(`${room.key}:${item.use}`);
    return Boolean(item.party);
}

function roomEnv(env, room, frame, flickerFloor) {
    let level = frame.levels[room.key] == null ? 1 : frame.levels[room.key];
    if (flickerFloor === room.floor && hashId(`f${Math.floor(env.now / 90)}`) % 3 === 0) level *= 0.12;
    if (level > 0.14 && level < 0.9 && Math.floor(env.now / 70) % 3 === 0) level *= 0.5;
    const local = Object.create(env);
    local.level = level;
    return local;
}

function drawItems(ctx, map, floor, env, frame, front, flickerFloor, lights) {
    const base = baseY(map, floor);
    floor.rooms.forEach((room) => {
        const local = roomEnv(env, room, frame, flickerFloor);
        room.items.forEach((item) => {
            const prop = PROPS[item.type];
            if (!prop) return;
            const isFront = prop.layer === 'front';
            if (isFront !== front) return;
            const on = itemOn(item, room, frame);
            const y = base - (item.lift || 0) * PX;
            const g = painter(ctx, room.x + item.x, y);
            if (front && prop.s) prop.s(g, item);
            if (prop.d) prop.d(g, item, local, on);
            if (lights && prop.light) {
                const light = prop.light(item, local, on);
                if (light) {
                    lights.push({
                        x: room.x + item.x + (light.dx || 0),
                        y: y - light.dy,
                        r: light.r,
                        color: light.color,
                        a: light.a,
                        cone: light.cone,
                        shaft: light.shaft,
                        lean: Math.max(-26, Math.min(26, (12.5 - env.hour) * 5)),
                    });
                }
            }
        });
    });
}

function drawDoors(ctx, map, floor, actors) {
    const top = floorTop(map, floor.index);
    floor.rooms.forEach((room, index) => {
        if (!index) return;
        const doorTop = top + FLOOR_H - BAND - DOOR_H;
        const open = actors.some((actor) => actor.mode !== 'ride' && actor.floor === floor.index && Math.abs(actor.x - room.x) < 52);
        if (open) {
            ctx.fillStyle = rgba('#8a5a32', 0.5);
            ctx.fillRect(room.x + 3, doorTop, 15, DOOR_H + 8);
            ctx.fillStyle = '#6b4228';
            ctx.fillRect(room.x - 3, doorTop, 6, 9);
        } else {
            ctx.fillStyle = '#8a5a32';
            ctx.fillRect(room.x - 3, doorTop, 6, DOOR_H + 8);
            ctx.fillStyle = '#b07a44';
            ctx.fillRect(room.x - 3, doorTop, 2, DOOR_H + 8);
            ctx.fillStyle = '#e0c070';
            ctx.fillRect(room.x - 5, doorTop + 84, 3, 6);
            ctx.fillRect(room.x + 2, doorTop + 84, 3, 6);
        }
    });
}

function drawGround(ctx, map, env) {
    const y = ROOF + map.floors.length * FLOOR_H;
    const dim = env.dark * 0.5;
    ctx.fillStyle = mix('#a8adb4', '#0c0e2c', dim);
    ctx.fillRect(-3000, y - 22, 9000, 32);
    ctx.fillStyle = mix('#c2c6cc', '#0c0e2c', dim);
    ctx.fillRect(-3000, y - 22, 9000, 4);
    ctx.fillStyle = mix('#7a7f88', '#0c0e2c', dim);
    for (let col = -3000; col < 6000; col += 66) ctx.fillRect(col, y - 18, 2, 28);
    ctx.fillRect(-3000, y + 10, 9000, 5);
    ctx.fillStyle = mix('#3c414c', '#0c0e2c', dim);
    ctx.fillRect(-3000, y + 15, 9000, 1200);
    ctx.fillStyle = mix('#d8d0a0', '#0c0e2c', dim);
    for (let col = -3000; col < 6000; col += 96) ctx.fillRect(col, y + 40, 48, 5);
    if (env.rain) {
        ctx.fillStyle = 'rgba(214,236,255,0.35)';
        for (let i = 0; i < 40; i += 1) {
            const seed = hashId(`charco-${i}`);
            if (Math.floor((env.now + seed) / 140) % 4 === 0) ctx.fillRect(-600 + (seed % (map.width + 1200)), y - 18 + ((seed >>> 8) % 26), 6, 2);
        }
    }
    const right = map.elevX + ELEV_W + BRICK;
    const spots = [[map.x0 - 150, 'tree', 1], [map.x0 - 330, 'tree', 2], [map.x0 - 520, 'tree', 3], [right + 150, 'tree', 4], [right + 340, 'tree', 5], [right + 540, 'tree', 6],
        [map.x0 - 236, 'bush', 1], [map.x0 - 420, 'bush', 2], [right + 244, 'bush', 3], [right + 440, 'bush', 4]];
    spots.forEach(([x, kind, seed]) => OUTDOOR[kind](painter(ctx, x, y - 14), env, seed));
    [map.x0 - 74, right + 74, map.x0 - 620, right + 640].forEach((x) => OUTDOOR.streetLamp(painter(ctx, x, y - 14), env));
}

function drawShell(ctx, map, env) {
    const top = ROOF;
    const height = map.floors.length * FLOOR_H;
    const bodyW = BRICK * 2 + map.innerW + ELEV_W;
    const right = map.elevX + ELEV_W;
    const tone = mix('#8d4a32', '#1a1430', env.dark * 0.45);
    brickColumn(ctx, map.x0, top, BRICK, height, tone);
    brickColumn(ctx, right, top, BRICK, height, tone);
    brickColumn(ctx, map.elevX - 4, top - 66, ELEV_W + BRICK + 4, 66, tone);
    ctx.fillStyle = mix('#5a4a44', '#1a1430', env.dark * 0.45);
    ctx.fillRect(map.x0 - 9, top - 15, bodyW + 18, 15);
    ctx.fillRect(map.x0 - 9, top + height, bodyW + 18, 9);
    ctx.fillRect(map.elevX - 10, top - 72, ELEV_W + BRICK + 16, 9);
    ctx.fillStyle = mix('#7a665e', '#1a1430', env.dark * 0.45);
    ctx.fillRect(map.x0 - 9, top - 15, bodyW + 18, 4);
    ctx.fillStyle = '#3a3038';
    ctx.fillRect(map.elevX + 18, top - 54, 30, 39);
    ctx.fillStyle = env.dark > 0.3 ? '#ffe7a0' : '#8fd0ea';
    ctx.fillRect(map.elevX + 66, top - 51, 27, 18);

    const roofY = top - 15;
    OUTDOOR.tank(painter(ctx, map.x0 + 64, roofY));
    OUTDOOR.acUnit(painter(ctx, map.x0 + bodyW * 0.7, roofY), env);
    if (bodyW > 900) OUTDOOR.acUnit(painter(ctx, map.x0 + bodyW * 0.7 - 70, roofY), env);
    OUTDOOR.billboard(painter(ctx, map.x0 + bodyW * 0.4, roofY), env);
    OUTDOOR.antenna(painter(ctx, map.elevX + ELEV_W * 0.75, top - 72), env);
    if (!env.rain && env.dark < 0.6) {
        [0.16, 0.6, 0.64].forEach((spot, index) => OUTDOOR.pigeon(painter(ctx, map.x0 + bodyW * spot, roofY), env, index * 7));
    }

    map.floors.forEach((floor) => {
        const y = floorTop(map, floor.index);
        ctx.fillStyle = '#1a140e';
        ctx.fillRect(map.x0 + 3, y + 18, BRICK - 6, 36);
        ctx.fillStyle = '#f6e27a';
        ctx.font = 'bold 10px monospace';
        ctx.fillText(floor.index === 0 ? 'PB' : String(floor.index), map.x0 + 6, y + 32);
        ctx.font = '8px monospace';
        ctx.fillText(String(floor.name || '').slice(0, 3), map.x0 + 6, y + 47);
    });
}

function glowAt(ctx, x, y, radius, color, alpha) {
    const halo = ctx.createRadialGradient(x, y, 0, x, y, radius);
    halo.addColorStop(0, rgba(color, alpha));
    halo.addColorStop(1, rgba(color, 0));
    ctx.globalCompositeOperation = 'lighter';
    ctx.fillStyle = halo;
    ctx.fillRect(x - radius, y - radius, radius * 2, radius * 2);
    ctx.globalCompositeOperation = 'source-over';
}

function drawOutdoorLights(ctx, map, env) {
    const y = ROOF + map.floors.length * FLOOR_H - 14;
    const right = map.elevX + ELEV_W + BRICK;
    const lamp = OUTDOOR.streetLampLight(env);
    if (lamp) [map.x0 - 74, right + 74, map.x0 - 620, right + 640].forEach((x) => glowAt(ctx, x + lamp.dx, y - lamp.dy, lamp.r, lamp.color, lamp.a));
    const beacon = OUTDOOR.antennaLight(env);
    if (beacon) glowAt(ctx, map.elevX + ELEV_W * 0.75 + 1, ROOF - 72 - beacon.dy, beacon.r, beacon.color, beacon.a);
    if (env.dark > 0.3) glowAt(ctx, map.x0 + (BRICK * 2 + map.innerW + ELEV_W) * 0.4, ROOF - 60, 190, '#ffe680', 0.3 * env.dark);
}

function shaftBack(ctx, map, car) {
    const x = map.elevX;
    const top = floorTop(map, map.floors.length - 1);
    const bottom = floorTop(map, 0) + FLOOR_H;
    ctx.fillStyle = '#232830';
    ctx.fillRect(x, top, ELEV_W, bottom - top);
    ctx.fillStyle = '#2f3640';
    ctx.fillRect(x + 9, top, ELEV_W - 18, bottom - top);
    ctx.fillStyle = '#5a6270';
    ctx.fillRect(x + 9, top, 3, bottom - top);
    ctx.fillRect(x + ELEV_W - 12, top, 3, bottom - top);
    map.floors.forEach((floor) => {
        const y = floorTop(map, floor.index);
        ctx.fillStyle = '#14181e';
        ctx.fillRect(x, y, ELEV_W, 8);
        ctx.fillStyle = '#434c58';
        [70, 140, 210].forEach((dy) => {
            ctx.fillRect(x + 6, y + dy, 9, 3);
            ctx.fillRect(x + ELEV_W - 15, y + dy, 9, 3);
        });
    });
    const carBottom = feetY(map, car.y) + FEET;
    const carTop = carBottom - CAR_H;
    ctx.fillStyle = '#0c0e12';
    ctx.fillRect(x + ELEV_W / 2 - 9, top, 2, Math.max(0, carTop - top));
    ctx.fillRect(x + ELEV_W / 2 + 7, top, 2, Math.max(0, carTop - top));
    const travel = Math.max(1, map.floors.length - 1);
    const weightY = top + 12 + (bottom - top - 90) * (car.y / travel);
    ctx.fillStyle = '#0c0e12';
    ctx.fillRect(x + ELEV_W - 9, top, 2, weightY - top);
    ctx.fillStyle = '#6a5a4a';
    ctx.fillRect(x + ELEV_W - 12, weightY, 9, 54);
    ctx.fillStyle = '#4a3f34';
    for (let row = 0; row < 54; row += 9) ctx.fillRect(x + ELEV_W - 12, weightY + row, 9, 2);

    const cx = x + 15;
    const cw = ELEV_W - 30;
    ctx.fillStyle = '#3a424c';
    ctx.fillRect(cx - 3, carTop - 6, cw + 6, 9);
    ctx.fillStyle = '#d8ccb4';
    ctx.fillRect(cx, carTop, cw, CAR_H - 9);
    ctx.fillStyle = '#c4b698';
    for (let i = 0; i < 3; i += 1) ctx.fillRect(cx + 6 + i * 28, carTop + 30, 22, 132);
    ctx.fillStyle = '#fff4c0';
    ctx.fillRect(cx + 18, carTop + 6, cw - 36, 5);
    ctx.fillStyle = '#9aa0a8';
    ctx.fillRect(cx + 3, carBottom - 84, cw - 6, 3);
    ctx.fillStyle = '#1a140e';
    ctx.fillRect(cx + cw - 27, carTop + 15, 21, 12);
    ctx.fillStyle = '#ff8a3c';
    ctx.font = 'bold 9px monospace';
    const shown = Math.round(car.y);
    ctx.fillText(shown === 0 ? 'PB' : String(shown), cx + cw - 24, carTop + 24);
    ctx.fillStyle = '#5a6270';
    ctx.fillRect(cx - 3, carBottom - 9, cw + 6, 9);
}

function shaftFront(ctx, map, car, actors) {
    const x = map.elevX;
    const carBottom = feetY(map, car.y) + FEET;
    const carTop = carBottom - CAR_H;
    const cw = ELEV_W - 30;
    ctx.fillStyle = '#7a828e';
    ctx.fillRect(x + 12, carTop, 4, CAR_H - 9);
    ctx.fillRect(x + 15 + cw - 1, carTop, 4, CAR_H - 9);
    map.floors.forEach((floor) => {
        const top = floorTop(map, floor.index);
        const doorTop = top + FLOOR_H - BAND - DOOR_H;
        ctx.fillStyle = '#3a424c';
        ctx.fillRect(x - 5, top + 8, 10, doorTop - top - 8);
        const here = Math.abs(car.y - floor.index) < 0.02;
        const open = here ? car.doors : 0;
        const tall = Math.round((DOOR_H + 8) * (1 - open));
        ctx.fillStyle = '#9aa0a8';
        ctx.fillRect(x - 4, doorTop, 8, tall);
        ctx.fillStyle = '#c8ccd2';
        ctx.fillRect(x - 4, doorTop, 2, tall);
        const called = actors.some((actor) => actor.mode === 'wait' && actor.floor === floor.index);
        ctx.fillStyle = '#1a140e';
        ctx.fillRect(x - 24, doorTop + 60, 9, 15);
        ctx.fillStyle = called ? '#ffb030' : '#5a4010';
        ctx.fillRect(x - 21, doorTop + 63, 3, 3);
        ctx.fillStyle = here ? '#3dff8a' : '#2a5a3a';
        ctx.fillRect(x - 21, doorTop + 69, 3, 3);
    });
}

const ICONS = {
    dots(ctx, x, y) {
        [0, 6, 12].forEach((dx) => ctx.fillRect(x + dx, y + 6, 3, 3));
    },
    exclaim(ctx, x, y) {
        ctx.fillStyle = '#e0503c';
        ctx.fillRect(x + 6, y, 3, 8);
        ctx.fillRect(x + 6, y + 10, 3, 3);
    },
    heart(ctx, x, y) {
        ctx.fillStyle = '#e0508a';
        ctx.fillRect(x + 1, y + 1, 5, 5);
        ctx.fillRect(x + 8, y + 1, 5, 5);
        ctx.fillRect(x + 3, y + 5, 9, 4);
        ctx.fillRect(x + 5, y + 9, 5, 3);
    },
    note(ctx, x, y) {
        ctx.fillStyle = '#5c6bc0';
        ctx.fillRect(x + 8, y, 2, 10);
        ctx.fillRect(x + 8, y, 6, 3);
        ctx.fillRect(x + 4, y + 8, 6, 4);
    },
    chart(ctx, x, y) {
        ctx.fillStyle = '#2e9a8a';
        ctx.fillRect(x + 1, y + 7, 3, 5);
        ctx.fillRect(x + 6, y + 3, 3, 9);
        ctx.fillRect(x + 11, y, 3, 12);
    },
    coffee(ctx, x, y) {
        ctx.fillStyle = '#6b3e2a';
        ctx.fillRect(x + 2, y + 4, 9, 8);
        ctx.fillRect(x + 11, y + 6, 3, 4);
        ctx.fillStyle = '#9aa0a8';
        ctx.fillRect(x + 4, y, 2, 3);
        ctx.fillRect(x + 8, y + 1, 2, 2);
    },
    zzz(ctx, x, y, now) {
        ctx.fillStyle = '#5c6bc0';
        ctx.font = 'bold 11px monospace';
        ctx.fillText(Math.floor(now / 600) % 2 ? 'zZ' : 'Zz', x, y + 11);
    },
};

function drawBubble(ctx, x, y, kind, now) {
    const icon = ICONS[kind];
    if (!icon) return;
    const bx = Math.round(x) + 9;
    const by = Math.round(y) - 21;
    ctx.fillStyle = INK;
    ctx.fillRect(bx - 3, by - 3, 27, 24);
    ctx.fillRect(bx, by + 21, 6, 3);
    ctx.fillStyle = '#fffdf6';
    ctx.fillRect(bx, by, 21, 18);
    ctx.fillRect(bx + 2, by + 18, 3, 3);
    ctx.fillStyle = INK;
    icon(ctx, bx + 3, by + 3, now);
}

/** Letrero con el nombre. `placed` guarda los ya puestos para apilar los que chocan. */
function drawPlate(ctx, actor, selfUid, now, placed) {
    const name = String(actor.person.nombre || '').trim().split(' ')[0] || 'Alguien';
    ctx.font = 'bold 10px monospace';
    const plateW = Math.ceil(ctx.measureText(name).width) + 17;
    const x = Math.round(actor.x - plateW / 2);
    let y = Math.round(actor.y) - (actor.hidden ? 176 : 141);
    for (let tries = 0; tries < 6; tries += 1) {
        const top = y;
        if (!placed.some((box) => x < box.x + box.w + 3 && x + plateW + 3 > box.x && Math.abs(top - box.y) < 15)) break;
        y -= 16;
    }
    placed.push({ x, y, w: plateW });
    ctx.fillStyle = rgba('#1a140e', actor.hidden ? 0.6 : 0.92);
    ctx.fillRect(x, y, plateW, 14);
    ctx.fillStyle = actor.working ? '#5ee0ff' : '#3dff8a';
    ctx.fillRect(x + 3, y + 4, 6, 6);
    ctx.fillStyle = '#f6e27a';
    ctx.fillText(name, x + 12, y + 11);
    if (selfUid && actor.person.uid === selfUid && Math.floor(now / 260) % 2 === 0) {
        ctx.fillStyle = '#ffe14a';
        ctx.fillRect(Math.round(actor.x) - 3, y - 12, 6, 6);
        ctx.fillRect(Math.round(actor.x) - 1, y - 6, 2, 3);
    }
    if (actor.bubble && !actor.hidden) drawBubble(ctx, actor.x, y - 3, actor.bubble, now);
}

function drawEventExtras(ctx, map, env, actors) {
    const event = env.event;
    if (!event) return;
    const t = Math.max(0, Math.min(1, event.age / event.ms));
    if (event.id === 'cat' || event.id === 'roomba') {
        const floor = map.floors[event.seed % map.floors.length];
        const forward = event.seed % 2 === 0;
        const span = map.innerW - 60;
        const x = map.innerX + 30 + (forward ? t : 1 - t) * span;
        const g = painter(ctx, x, baseY(map, floor) + 3);
        if (event.id === 'cat') CRITTERS.cat(g, env, forward ? 1 : -1);
        else CRITTERS.roomba(g, env);
        if (event.id === 'cat') {
            actors.forEach((actor) => {
                if (actor.floor === floor.index && actor.mode !== 'ride' && Math.abs(actor.x - x) < 90) actor.bubble = 'heart';
            });
        }
    }
    if (event.id === 'birthday') {
        const room = map.commonRooms.find((item) => item.kind === 'lounge');
        if (!room) return;
        const floor = map.floors[room.floor];
        const base = baseY(map, floor);
        for (let i = 0; i < 7; i += 1) {
            CRITTERS.balloon(painter(ctx, room.x + 30 + i * ((room.w - 60) / 6), base - 132 - (i % 3) * 12), env, i);
        }
        for (let i = 0; i < 46; i += 1) {
            const seed = hashId(`confeti-${i}`);
            const fall = (env.now / (6 + (seed % 5)) + seed) % 204;
            ctx.fillStyle = CONFETTI[seed % CONFETTI.length];
            ctx.fillRect(room.x + 9 + ((seed >>> 4) % (room.w - 18)) + Math.round(Math.sin(fall / 14 + seed) * 6), base - 222 + fall, 3, seed % 2 ? 3 : 6);
        }
    }
}

/**
 * Pinta un cuadro.
 * frame: lo que devuelve stepSim. wall: hora real en ms. camera: { zoom, panX,
 * panY, stageW, stageH, dpr }. opts.hour fuerza la hora del día en pruebas.
 */
export function drawOffice(ctx, map, frame, wall, selfUid, event, camera, opts) {
    const hits = [];
    const view = camera || { zoom: 1, panX: 0, panY: 0, stageW: map.width, stageH: 640, dpr: 1 };
    const dpr = view.dpr || 1;
    const actors = frame.actors;
    const date = new Date(wall);
    const hour = opts && opts.hour != null ? opts.hour : date.getHours() + date.getMinutes() / 60 + date.getSeconds() / 3600;
    const storm = Boolean(event && event.id === 'storm');
    const strike = storm ? event.age % 3100 : -1;
    const sky = skyAt(hour, storm);
    const env = {
        now: wall,
        wall,
        hour,
        sky,
        dark: sky.dark,
        storm,
        rain: storm,
        flash: storm && (strike < 110 || (strike > 260 && strike < 330)),
        event: event || null,
        level: 1,
    };
    if (!lightLayer) lightLayer = createLightLayer();
    if (!map.cache) map.cache = [];

    ctx.imageSmoothingEnabled = false;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, view.stageW, view.stageH);
    drawSky(ctx, view.stageW, view.stageH, env, -view.panX * 0.12);
    ctx.setTransform(dpr * view.zoom, 0, 0, dpr * view.zoom, view.panX * dpr, view.panY * dpr);

    drawGround(ctx, map, env);
    drawShell(ctx, map, env);
    drawOutdoorLights(ctx, map, env);

    const visible = map.floors.filter((floor) => {
        const y = floorTop(map, floor.index) * view.zoom + view.panY;
        return y < view.stageH + 20 && y + FLOOR_H * view.zoom > -20;
    });
    const flickerFloor = event && event.id === 'flicker' ? event.seed % map.floors.length : -1;
    const lightsByFloor = {};

    visible.forEach((floor) => {
        if (!map.cache[floor.index]) map.cache[floor.index] = buildFloorCache(map, floor);
        ctx.drawImage(map.cache[floor.index], map.innerX, floorTop(map, floor.index));
        lightsByFloor[floor.index] = [];
        drawItems(ctx, map, floor, env, frame, false, flickerFloor, lightsByFloor[floor.index]);
    });

    shaftBack(ctx, map, frame.car);
    actors.forEach((actor) => {
        if (actor.mode === 'ride') blitAvatar(ctx, actor.x, actor.y, actor.person.uid, actor.pose, actor.facing, env.now, actor.stride);
    });
    shaftFront(ctx, map, frame.car, actors);

    visible.forEach((floor) => {
        drawDoors(ctx, map, floor, actors);
        actors
            .filter((actor) => actor.mode !== 'ride' && actor.floor === floor.index && !actor.hidden)
            .sort((a, b) => a.x - b.x)
            .forEach((actor) => blitAvatar(ctx, actor.x, actor.y, actor.person.uid, actor.pose, actor.facing, env.now, actor.stride));
        drawItems(ctx, map, floor, env, frame, true, flickerFloor, lightsByFloor[floor.index]);
    });
    drawEventExtras(ctx, map, env, actors);

    const blackout = event && event.id === 'blackout';
    visible.forEach((floor) => {
        const top = floorTop(map, floor.index);
        const zones = floor.rooms.map((room) => {
            const level = roomEnv(env, room, frame, flickerFloor).level;
            const day = 0.26 - 0.24 * level;
            const night = 0.7 - 0.38 * level;
            return { x: room.x, w: room.w, alpha: Math.min(0.86, day + (night - day) * env.dark + (blackout ? 0.3 : 0)) };
        });
        zones.push({ x: map.elevX, w: ELEV_W, alpha: 0.2 + 0.4 * env.dark + (blackout ? 0.2 : 0) });
        const lights = lightsByFloor[floor.index];
        const carY = feetY(map, frame.car.y) + FEET - CAR_H + 9;
        if (!blackout) lights.push({ x: map.elevX + ELEV_W / 2, y: carY, r: 150, color: '#fff0b8', a: 0.6 });
        lightFloor(ctx, lightLayer, map.innerX, top, map.innerW + ELEV_W, FLOOR_H, zones, lights);
    });

    const placed = [];
    [...actors].sort((a, b) => a.x - b.x).forEach((actor) => {
        const shown = visible.some((floor) => floor.index === actor.floor) || actor.mode === 'ride';
        actor.sx = actor.x;
        actor.sy = actor.y - 80;
        hits.push({ kind: 'person', person: actor.person, sx: actor.x, sy: actor.y - 66 });
        if (shown) drawPlate(ctx, actor, selfUid, env.now, placed);
    });
    map.floors.forEach((floor) => floor.rooms.forEach((room) => {
        if (room.projectId) hits.push({ kind: 'desk', projectId: room.projectId, x: room.x, y: floorTop(map, floor.index), w: room.w - 8, h: FLOOR_H });
    }));

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    if (env.flash) {
        ctx.fillStyle = 'rgba(255,255,255,0.3)';
        ctx.fillRect(0, 0, view.stageW, view.stageH);
    }
    if (event && event.age < 6500) {
        ctx.font = 'bold 11px monospace';
        const width = Math.min(view.stageW - 20, Math.ceil(ctx.measureText(event.line).width) + 14);
        ctx.fillStyle = '#1a140e';
        ctx.fillRect(10, 10, width, 19);
        ctx.fillStyle = '#ffe14a';
        ctx.fillText(event.line, 17, 23);
    }
    return hits;
}
